/**
 * Step 1: Basic Details Component
 *
 * Enhanced job post basic details form with:
 * - Auto-save via React Hook Form
 * - AI-generated data pre-fill
 * - Comprehensive currency support
 * - Real-time validation
 * - Progress tracking
 * - Card layout with scrollable content
 *
 * UI identical to V2, form management uses React Hook Form
 */

import React, { useMemo } from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  Form,
  Input,
  DatePicker,
  Row,
  Col,
  Card,
  Tooltip,
  Radio,
  Typography,
  Alert,
  Checkbox,
  Switch,
  Space,
  Flex,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { InfoCircleOutlined } from "@ant-design/icons";
import { JobPostFormData } from "../../schemas/jobPostValidation";
import {
  calculateCompletionPercentage,
  validateStep1Complete,
} from "../../hooks/useFormValidation";
import CTCField from "./components/CTCField";
import StepFooter from "../../components/StepFooter";
import { PersistenceStatus } from "../../hooks/useFormPersistence";
import "./index.scss";

interface Step1BasicDetailsProps {
  currentStep: number;
  totalSteps: number;
  prompt: string;
  isGenerating: boolean;
  onPromptChange: (value: string) => void;
  onGenerate: () => void;
  onPrevious: () => void;
  onNext: () => void;
  isPreviousDisabled?: boolean;
  isNextDisabled?: boolean;
  stepTitles: string[];
  isNavigating?: boolean;
  saveStatus: PersistenceStatus;
}

const Step1BasicDetails: React.FC<Step1BasicDetailsProps> = ({
  currentStep,
  totalSteps,
  prompt,
  isGenerating,
  onPromptChange,
  onGenerate,
  onPrevious,
  onNext,
  isPreviousDisabled = false,
  isNextDisabled = false,
  stepTitles,
  isNavigating = false,
  saveStatus,
}) => {
  const { control, watch } = useFormContext<JobPostFormData>();
  const endDate = watch("step1.endDate");

  // Calculate days until end date for warning
  const daysUntilEndDate = useMemo(() => {
    if (!endDate) return null;
    const selectedDate = dayjs(endDate);
    const today = dayjs().startOf("day");
    const daysDiff = selectedDate.diff(today, "day");
    return daysDiff >= 0 ? daysDiff : null;
  }, [endDate]);

  // Notice period options for Radio Group
  const noticePeriodOptions: Record<string, string> = {
    "0": "Immediate",
    "15": "15 days",
    "30": "30 days",
    "60": "60 days",
    "90": "90 days",
  };

  return (
    <div className="step1-container">
      <Card
        className="step1-card"
        title={
          <div>
            <Typography.Title
              level={5}
              style={{ marginTop: 0, marginBottom: 4, fontWeight: 600 }}
            >
              Basic Requirements
            </Typography.Title>
            <Typography.Text type="secondary">
              Please fill in the basic details of the job post.
            </Typography.Text>
          </div>
        }
      >
        {/* Scrollable Card Body */}
        <div className="step1-card-body">
          {/* Form - Keep Form wrapper for styling */}
          <Form
            layout="vertical"
            variant="outlined"
            labelAlign="left"
            labelWrap
            colon={false}
            scrollToFirstError
            className="step1-form"
          >
            <Row gutter={[16, 16]}>
              {/* Job Title */}
              <Col xs={24} lg={12}>
                <Controller
                  name="step1.title"
                  control={control}
                  rules={{
                    required: "Job title is required",
                  }}
                  render={({ field, fieldState }) => (
                    <Form.Item
                      label={
                        <span>
                          Job Title{" "}
                          <span className="required-indicator">*</span>
                          <Tooltip title="Include experience level and key technologies for better visibility">
                            <InfoCircleOutlined
                              className="info-icon"
                              style={{ marginLeft: 4 }}
                            />
                          </Tooltip>
                        </span>
                      }
                      validateStatus={fieldState.error ? "error" : ""}
                      help={fieldState.error?.message}
                    >
                      <Input
                        {...field}
                        placeholder="Senior Full Stack Developer - React & Node.js"
                        variant="outlined"
                      />
                    </Form.Item>
                  )}
                />
              </Col>

              {/* Position */}
              <Col xs={24} lg={12}>
                <Controller
                  name="step1.position"
                  control={control}
                  rules={{
                    required: "Position is required",
                  }}
                  render={({ field, fieldState }) => (
                    <Form.Item
                      label={
                        <span>
                          Position/Role{" "}
                          <span className="required-indicator">*</span>
                        </span>
                      }
                      validateStatus={fieldState.error ? "error" : ""}
                      help={fieldState.error?.message}
                    >
                      <Input
                        {...field}
                        placeholder="Software Engineer"
                        variant="outlined"
                      />
                    </Form.Item>
                  )}
                />
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              {/* Number of Openings */}
              <Col xs={24} lg={12}>
                <Controller
                  name="step1.numberOfOpenings"
                  control={control}
                  rules={{
                    required: "Number of openings is required",
                    validate: (value) => {
                      if (value === null || value === undefined) {
                        return "Number of openings is required";
                      }
                      const numValue =
                        typeof value === "number" ? value : Number(value);
                      if (isNaN(numValue)) {
                        return "Number of openings is required";
                      }
                      if (numValue <= 0) {
                        return "Number of openings must be greater than 0";
                      }
                      if (numValue > 1000) {
                        return "Number of openings cannot exceed 1000";
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <Form.Item
                      label={
                        <span>
                          Number of Available Positions{" "}
                          <span className="required-indicator">*</span>
                        </span>
                      }
                      validateStatus={fieldState.error ? "error" : ""}
                      help={fieldState.error?.message}
                    >
                      <Input
                        {...field}
                        type="number"
                        placeholder="Enter number of positions"
                        min={1}
                        max={1000}
                        step={1}
                        variant="outlined"
                        value={field.value ?? ""}
                        onChange={(e) => {
                          const value =
                            e.target.value === ""
                              ? null
                              : parseInt(e.target.value, 10);
                          field.onChange(value);
                        }}
                      />
                    </Form.Item>
                  )}
                />
              </Col>

              {/* Notice Period */}
              <Col xs={24} lg={12}>
                <Controller
                  name="step1.noticePeriod"
                  control={control}
                  defaultValue="0"
                  render={({ field }) => (
                    <Form.Item label="Notice Period / Candidate Availability (in Days)">
                      <Radio.Group
                        {...field}
                        className="notice-period-radio-group"
                        size="middle"
                        optionType="button"
                        buttonStyle="solid"
                      >
                        {Object.entries(noticePeriodOptions).map(
                          ([value, label]) => (
                            <Radio.Button key={value} value={value}>
                              {label}
                            </Radio.Button>
                          )
                        )}
                      </Radio.Group>
                    </Form.Item>
                  )}
                />
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              {/* Compensation (Currency + CTC) - 50% width */}
              <Col xs={24} lg={12}>
                <CTCField control={control} />
              </Col>

              {/* End Date - 50% width */}
              <Col xs={24} lg={12}>
                <Controller
                  name="step1.endDate"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Form.Item
                      label={
                        <span>
                          End Date
                          <Tooltip title="Setting an end date will automatically make the job post in-active on the specified date.">
                            <InfoCircleOutlined
                              className="info-icon"
                              style={{ marginLeft: 4 }}
                            />
                          </Tooltip>
                        </span>
                      }
                      validateStatus={fieldState.error ? "error" : ""}
                      help={fieldState.error?.message}
                    >
                      <DatePicker
                        {...field}
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) => {
                          field.onChange(
                            date ? date.format("YYYY-MM-DD") : null
                          );
                        }}
                        placeholder="Select end date"
                        style={{ width: "100%" }}
                        variant="outlined"
                        format="DD-MM-YYYY"
                        disabledDate={(current) => {
                          return current && current < dayjs().startOf("day");
                        }}
                      />
                      {daysUntilEndDate !== null && daysUntilEndDate <= 7 && daysUntilEndDate >= 0 && (
                        <Alert
                          message={
                            daysUntilEndDate === 0
                              ? "Job post will close today"
                              : daysUntilEndDate === 1
                                ? "Job post will close tomorrow"
                                : `Job post will close in ${daysUntilEndDate} days`
                          }
                          type="warning"
                          showIcon
                          style={{ marginTop: 8 }}
                        />
                      )}
                    </Form.Item>
                  )}
                />
              </Col>
            </Row>

            {/* Description */}
            <Row>
              <Col xs={24}>
                <Controller
                  name="step1.description"
                  control={control}
                  rules={{
                    required: "Description is required",
                  }}
                  render={({ field, fieldState }) => (
                    <Form.Item
                      label={
                        <span>
                          Requirement in Brief{" "}
                          <span className="required-indicator">*</span>
                          <Tooltip title="Provide a concise overview of the job requirements and responsibilities.">
                            <InfoCircleOutlined
                              className="info-icon"
                              style={{ marginLeft: 4 }}
                            />
                          </Tooltip>
                        </span>
                      }
                      validateStatus={fieldState.error ? "error" : ""}
                      help={fieldState.error?.message}
                    >
                      <TextArea
                        {...field}
                        placeholder="In this role, you will work across the entire web stack, leveraging MongoDB, Express.js, React, Node.js..."
                        autoSize={{ minRows: 6, maxRows: 12 }}
                        variant="outlined"
                      />
                    </Form.Item>
                  )}
                />
              </Col>
            </Row>

            {/* Notifications */}
            <Row>
              <Col xs={24}>
                <Form.Item label="Candidate Notifications">
                  <Flex align="start" gap={16} vertical>
                    <Controller
                      name="step1.notifications.email"
                      control={control}
                      render={({ field }) => (
                        // <Checkbox
                        //   checked={field.value}
                        //   onChange={(e) => field.onChange(e.target.checked)}
                        //   style={{ marginRight: 24 }}
                        // >
                        //   Email
                        // </Checkbox>
                        <Space align="center" size={8}>
                          
                          <Switch
                            checked={field.value === true}
                            onChange={(checked) => field.onChange(checked)}
                          />
<Typography.Text type="secondary">Email</Typography.Text>
                        </Space>)}
                    />
                    <Controller
                      name="step1.notifications.whatsapp"
                      control={control}
                      render={({ field }) => (
                        // <Checkbox
                        //   checked={field.value}
                        //   onChange={(e) => field.onChange(e.target.checked)}
                        // >
                        //   WhatsApp
                        // </Checkbox>
                        <Space align="center" size={8}>
                          
                          <Switch
                            checked={field.value === true}
                            onChange={(checked) => field.onChange(checked)}
                          />
                          <Typography.Text type="secondary">WhatsApp</Typography.Text>
                        </Space>
                      )}
                    />
                  </Flex>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
        <div className="step1-card-footer">
          <StepFooter
            currentStep={currentStep}
            totalSteps={totalSteps}
            prompt={prompt}
            isGenerating={isGenerating}
            onPromptChange={onPromptChange}
            onGenerate={onGenerate}
            onPrevious={onPrevious}
            onNext={onNext}
            isPreviousDisabled={isPreviousDisabled}
            isNextDisabled={isNextDisabled}
            stepTitles={stepTitles}
            isNavigating={isNavigating}
            saveStatus={saveStatus}
          />
        </div>
      </Card>
    </div>
  );
};

Step1BasicDetails.displayName = "Step1BasicDetails";

export default Step1BasicDetails;
